import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { 
  Building2, 
  Truck, 
  ArrowRight, 
  CheckCircle, 
  Users, 
  Shield,
  ArrowLeft
} from 'lucide-react';
import HospitalRegistrationForm from '../components/registration/HospitalRegistrationForm';
import FleetOperatorRegistrationForm from '../components/registration/FleetOperatorRegistrationForm';

const NewUserRegistrationPage = ({ dashboardType = 'admin' }) => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [registrationData, setRegistrationData] = useState(null);

  // Get dashboard-specific branding
  const getDashboardBranding = () => {
    switch (dashboardType) {
      case 'admin':
        return {
          primaryColor: 'bg-red-600',
          primaryColorHover: 'hover:bg-red-700',
          primaryColorText: 'text-red-600',
          primaryColorBorder: 'border-red-600',
          primaryColorBg: 'bg-red-600'
        };
      case 'hospital':
        return {
          primaryColor: 'bg-blue-600',
          primaryColorHover: 'hover:bg-blue-700',
          primaryColorText: 'text-blue-600',
          primaryColorBorder: 'border-blue-600',
          primaryColorBg: 'bg-blue-600'
        };
      case 'fleet':
        return {
          primaryColor: 'bg-green-600',
          primaryColorHover: 'hover:bg-green-700',
          primaryColorText: 'text-green-600',
          primaryColorBorder: 'border-green-600',
          primaryColorBg: 'bg-green-600'
        };
      case 'commandCenter':
        return {
          primaryColor: 'bg-purple-600',
          primaryColorHover: 'hover:bg-purple-700',
          primaryColorText: 'text-purple-600',
          primaryColorBorder: 'border-purple-600',
          primaryColorBg: 'bg-purple-600'
        };
      default:
        return {
          primaryColor: 'bg-[#1e3a8a]',
          primaryColorHover: 'hover:bg-[#1e3a8a]/90',
          primaryColorText: 'text-[#1e3a8a]',
          primaryColorBorder: 'border-[#1e3a8a]',
          primaryColorBg: 'bg-[#1e3a8a]'
        };
    }
  };

  const branding = getDashboardBranding();

  const userTypes = [
    {
      id: 'hospital',
      title: 'Hospital Registration',
      description: 'Register a new hospital with admin user account',
      icon: Building2,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-50',
      features: [
        'Hospital profile management',
        'Admin user account creation',
        'License and compliance tracking',
        'Contact information management'
      ]
    },
    {
      id: 'fleet',
      title: 'Fleet Operator Registration',
      description: 'Register a new fleet operator with admin user account',
      icon: Truck,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-50',
      features: [
        'Fleet operator profile setup',
        'Business license management',
        'Service area configuration',
        'Emergency contact setup'
      ]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'Enterprise-grade security with encrypted data transmission'
    },
    {
      icon: Users,
      title: 'Role-Based Access',
      description: 'Different access levels for admins, operators, and drivers'
    },
    {
      icon: Truck,
      title: 'Real-time Operations',
      description: 'Live tracking and instant communication across all users'
    }
  ];

  const handleTypeSelection = (typeId) => {
    setSelectedType(typeId);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleBackToSelection = () => {
    setSelectedType(null);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSuccess = (result) => {
    setSuccessMessage(result.message || 'Registration completed successfully!');
    setErrorMessage('');
    setRegistrationData(result);
    
    // Don't redirect immediately - let user see the success message and details
    // They can manually go back to dashboard or register another organization
  };

  const handleError = (error) => {
    setErrorMessage(error);
    setSuccessMessage('');
    setRegistrationData(null);
  };

  const renderRegistrationForm = () => {
    const selectedUserType = userTypes.find(type => type.id === selectedType);
    const Icon = selectedUserType?.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="space-y-4 sm:space-y-6">
            
            {/* Header with Back Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <Button
                onClick={handleBackToSelection}
                variant="outline"
                className="flex items-center space-x-2 w-full sm:w-auto text-xs sm:text-sm"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Back to Selection</span>
              </Button>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 ${branding.primaryColorBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900 break-words">{selectedUserType?.title}</h1>
                  <p className="text-xs sm:text-sm text-gray-600 break-words">{selectedUserType?.description}</p>
                </div>
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-green-800 font-medium break-words">{successMessage}</p>
                </div>
                
                {/* Registration Details */}
                {registrationData && (
                  <div className="bg-white border border-green-200 rounded-lg p-3 mb-3">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                      {selectedType === 'hospital' ? 'Hospital' : 'Fleet Operator'} Details
                    </h4>
                    <div className="text-xs sm:text-sm text-gray-700 space-y-1">
                      {selectedType === 'hospital' ? (
                        <>
                          <p className="break-words"><strong>Hospital:</strong> {registrationData.data?.hospital?.name}</p>
                          <p className="break-words"><strong>Admin:</strong> {registrationData.data?.hospitalUser?.name}</p>
                          <p className="break-all"><strong>Email:</strong> {registrationData.data?.hospitalUser?.email}</p>
                          <p className="break-words"><strong>Temporary Password:</strong> {registrationData.temporaryPassword}</p>
                        </>
                      ) : (
                        <>
                          <p className="break-words"><strong>Fleet Operator:</strong> {registrationData.data?.operator?.operator_name}</p>
                          <p className="break-words"><strong>Admin:</strong> {registrationData.data?.adminUser?.name}</p>
                          <p className="break-all"><strong>Email:</strong> {registrationData.data?.adminUser?.email}</p>
                          <p className="break-words"><strong>Temporary Password:</strong> {registrationData.temporaryPassword}</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={() => navigate('/admin')}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm w-full sm:w-auto"
                  >
                    Back to Dashboard
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedType(null);
                      setSuccessMessage('');
                      setErrorMessage('');
                      setRegistrationData(null);
                    }}
                    variant="outline"
                    className="text-xs sm:text-sm w-full sm:w-auto"
                  >
                    Register Another
                  </Button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0">⚠️</div>
                  <p className="text-xs sm:text-sm text-red-800 break-words">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Registration Form */}
            {selectedType === 'hospital' && (
              <HospitalRegistrationForm
                onSuccess={handleSuccess}
                onError={handleError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            )}

            {selectedType === 'fleet' && (
              <FleetOperatorRegistrationForm
                onSuccess={handleSuccess}
                onError={handleError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTypeSelection = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Benefits & Branding */}
          <div className="hidden lg:block space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 ${branding.primaryColorBg} rounded-xl flex items-center justify-center`}>
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Smart Ambulance</h1>
              </div>
              <p className="text-lg font-semibold text-gray-600 max-w-md">
                Register your organization and start managing ambulance operations efficiently.
              </p>
            </div>

            <div className="space-y-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className={`w-10 h-10 ${branding.primaryColorBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Registration Type Selection */}
          <div className="w-full max-w-md mx-auto lg:max-w-none">
            <Card className="shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-2 text-center pb-6 sm:pb-8 px-4 sm:px-6 pt-4 sm:pt-6">
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4 lg:hidden">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 ${branding.primaryColorBg} rounded-lg flex items-center justify-center`}>
                    <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">Smart Ambulance</h1>
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">New User Registration</CardTitle>
                <CardDescription className="text-sm sm:text-base text-gray-600">
                  Choose your organization type to get started
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-4 sm:pb-6">
                <div className="space-y-3 sm:space-y-4">
                  {userTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <div
                        key={type.id}
                        onClick={() => handleTypeSelection(type.id)}
                        className={`p-4 sm:p-6 rounded-xl border-2 border-gray-200 cursor-pointer transition-all duration-200 ${branding.primaryColorBorder} hover:shadow-lg group ${type.hoverColor}`}
                      >
                        <div className="flex items-start space-x-3 sm:space-x-4">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 ${type.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 break-words">{type.title}</h3>
                            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 break-words">{type.description}</p>
                            <ul className="space-y-1">
                              {type.features.map((feature, index) => (
                                <li key={index} className="text-xs text-gray-500 flex items-center">
                                  <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                                  <span className="break-words">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 ${branding.primaryColorText} transition-colors duration-200 flex-shrink-0`} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Back to Login */}
                <div className="text-center pt-3 sm:pt-4">
                  <p className="text-xs sm:text-sm text-gray-600">
                    Already have an account?{' '}
                    <button
                      onClick={() => navigate('/login')}
                      className={`${branding.primaryColorText} hover:opacity-80 font-medium`}
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  return selectedType ? renderRegistrationForm() : renderTypeSelection();
};

export default NewUserRegistrationPage;
